import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import usePostSubmitTicket from '@/hooks/useApi/usePostSubmitTicket';
import TicketCreationForm from './TicketCreationForm';
import TicketCreationContext, { TicketCreationCtx } from './TicketCreationContext';
import TicketCreationDialogue from './TicketCreationDialogue';
import { TicketCreationDialogueHandlers, DialogueHandlerProps } from '../types';
import { useClientLogin } from '@/providers/ClientLogin.provider';
import useFetchTicketCategoryTypes from '@/hooks/useApi/useFetchTicketCategoryTypes';
import useFetchAccountUsers from '@/hooks/useApi/useFetchAccountUsers';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';

type FC = FunctionComponent;

export type LocationStateProps = {
  ticketSubject: string;
  ticketCategory?: string;
  ticketSubCategory?: string;
};

const TicketCreationContainer: FC = () => {
  const history = useHistory();
  const location = useLocation<LocationStateProps>();
  const { userSettings } = useUserSettingsProvider();
  const [createTicketError, setTicketCreateError] = useState('');
  const [showCreateTicketErrorAlert, setShowTicketCreateErrorAlert] = useState(false);
  const [showCreateTicketSuccessAlert, setShowTicketCreateSuccessAlert] = useState(false);
  const [shouldCreateTicket, setShouldCreateTicket] = useState(false);
  const [createTicketResponse, createTicket] = usePostSubmitTicket({});
  const [attachmentFileName, setAttachmentFileName] = useState('');
  const [fetchUsersResponse, fetchUsers] = useFetchAccountUsers({});
  const { ticketCategories, ticketSubCategories, fetchTicketCategoryTypes } = useFetchTicketCategoryTypes();
  const [users, setUsers] = useState({});

  useEffect(() => {
    fetchUsers({
      Timezone: userSettings.timezone,
    });
    fetchTicketCategoryTypes();
  }, []);

  useEffect(() => {
    if (fetchUsersResponse.Success === true) {
      setUsers(fetchUsersResponse.Users || {});
    }
  }, [fetchUsersResponse]);

  const {
    response: { LoginUserName },
  } = useClientLogin();
  const leadCreation: TicketCreationCtx = {
    showCreateTicketErrorAlert,
    showCreateTicketSuccessAlert,
    createTicketError,
  };

  const DefaultTicketValues: vp.SubmitTicketRequest = {
    AccountName: '',
    CcEmails: '',
    TicketCategory: location.state && location.state.ticketCategory ? location.state.ticketCategory : '7',
    TicketContent: '',
    TicketSubject: location.state && location.state.ticketSubject ? location.state.ticketSubject : '',
    SenderEmailAddress: '',
    TicketRequesterName: '',
    TicketAttachment: '',
    TransactionID: '',
    TicketSubCategory:
      location.state && location.state.ticketSubCategory ? location.state.ticketSubCategory : 'General inquiry',
    AttachmentFileName: '',
  };

  useEffect(() => {
    if (shouldCreateTicket === true) {
      const { Success, ErrorMessage } = createTicketResponse;

      if (Success === true) {
        setShowTicketCreateSuccessAlert(true);
      } else {
        setTicketCreateError(ErrorMessage);
        setShowTicketCreateErrorAlert(true);
      }
      setShouldCreateTicket(false);
    }
  }, [createTicketResponse]);

  const handlers = {
    handleDialogue: (handlerProps: DialogueHandlerProps<TicketCreationDialogueHandlers>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: TicketCreationDialogueHandlers = {
        create: {
          success: {
            default: () => {
              setShowTicketCreateSuccessAlert(false);
              history.push('/tickets');
            },
          },
          fail: {
            default: () => {
              setShowTicketCreateErrorAlert(false);
              setTicketCreateError('');
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
    updateFileName: (fileName: string) => {
      setAttachmentFileName(fileName);
    },
    onCreateLead: (values: vp.SubmitTicketRequest) => {
      setShouldCreateTicket(true);
      const ticket = { ...values };
      if (ticket.CcEmails !== '') {
        ticket.CcEmails = ticket.CcEmails.replaceAll('|', ',');
      }
      ticket.SenderEmailAddress = LoginUserName;
      ticket.TicketRequesterName = LoginUserName;
      ticket.AttachmentFileName = attachmentFileName ?? '';
      createTicket(ticket);
    },
  };

  return (
    <TicketCreationContext.Provider value={leadCreation}>
      <TicketCreationForm
        ticketCategories={ticketCategories}
        ticketSubCategories={ticketSubCategories}
        Users={Object.values(users || {})}
        defaultValues={DefaultTicketValues}
        handlers={handlers}
      />
      <TicketCreationDialogue handleDialogue={handlers.handleDialogue} />
    </TicketCreationContext.Provider>
  );
};

export default TicketCreationContainer;
